import type { VoxelData, VoxelMaterial } from '@/voxelTypes'

/**
 * Which document voxels a liquid sim treats as liquid, and how a liquid cell is written back. A
 * sim, its change sets and the reducer that applies them share one kind: the sim reads every
 * voxel that `matches` as liquid (everything else is terrain), and every liquid cell it writes is
 * `voxel(color)`. Kinds are plain data plus a cache, so the reducer can use them without the sim.
 */
export type LiquidKind = {
  /** Readable id (`type` or `type:material`). */
  readonly id: string
  /** True for document voxels that are this liquid. */
  matches(v: VoxelData | undefined): boolean
  /** The shared, frozen voxel a liquid cell of `color` writes (it always `matches`). */
  voxel(color: number): VoxelData
  /** Colour of a matching voxel that has none. */
  readonly defaultColor: number
}

/**
 * A liquid kind for one voxel type (optionally one material of it): `{ type, color, material }`
 * cells are the liquid. `material` undefined matches every material of the type and writes none
 * (the type's default).
 */
export function liquidKind(type: string, opts: { material?: VoxelMaterial; defaultColor?: number } = {}): LiquidKind {
  const { material } = opts
  const cache = new Map<number, VoxelData>()
  return Object.freeze({
    id: material ? `${type}:${material}` : type,
    defaultColor: opts.defaultColor ?? 0xffffff,
    matches: (v: VoxelData | undefined) => v !== undefined && v.type === type && (material === undefined || v.material === material),
    voxel: (color: number) => {
      const c = color >>> 0
      let v = cache.get(c)
      if (!v) {
        v = Object.freeze(material ? { type, color: c, material } : { type, color: c }) as VoxelData
        cache.set(c, v)
      }
      return v
    },
  })
}

/** Voxel type string of the poured liquid: tint (src/voxels/tint; saved as 'ink' before the rename). */
export const TINT_TYPE = 'tint'

/** Stained-glass ink (Spill): the default kind of the sim and of the change helpers. */
export const TINT_LIQUID: LiquidKind = liquidKind(TINT_TYPE)
